import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn } from '../../../utils/cn';

const buttonVariants = tv({
  base: [
    'inline-flex items-center justify-center rounded-lg font-medium focus:outline-none',
    'disabled:opacity-50 disabled:pointer-events-none',
    'transition transform duration-150 ease-out'
  ],
  variants: {
    variant: {
      primary: [
        'bg-[var(--accent-primary)] text-white shadow-[0_10px_24px_color-mix(in_srgb,var(--shadow) 24%,transparent)]',
        'hover:-translate-y-[1px] hover:scale-[1.03] hover:shadow-[0_14px_32px_color-mix(in_srgb,var(--shadow) 32%,transparent)]',
        'active:translate-y-0 active:scale-[0.99]',
        'focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]'
      ].join(' '),
      gradient: 'bg-[var(--accent-primary)] text-white',
      secondary: [
        'bg-[var(--bg-elev)] text-[var(--text)] border border-[var(--border-subtle)] shadow-[0_4px_12px_color-mix(in_srgb,var(--shadow) 14%,transparent)]',
        'hover:-translate-y-[1px] hover:scale-[1.02] hover:border-[var(--border-strong)] hover:shadow-[0_8px_18px_color-mix(in_srgb,var(--shadow) 22%,transparent)]',
        'focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]'
      ].join(' '),
      icon: 'p-2 bg-transparent theme-text hover:bg-[color-mix(in_srgb,var(--accent-primary)15%,transparent)] rounded-full focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
      destructive: [
        'bg-[var(--accent-secondary)] text-white shadow-[0_8px_20px_color-mix(in_srgb,var(--shadow) 22%,transparent)]',
        'hover:-translate-y-[1px] hover:scale-[1.02] hover:bg-[color-mix(in_srgb,var(--accent-secondary) 86%, #ffffff 14%)]',
        'active:translate-y-0 active:scale-[0.99]',
        'focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent-secondary) 65%, transparent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]'
      ].join(' '),
      outline: [
        'bg-transparent text-[var(--text)] border border-[var(--border-subtle)]',
        'hover:border-[var(--border-strong)] hover:bg-[color-mix(in_srgb,var(--bg-muted)40%,transparent)]',
        'focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]'
      ].join(' '),
      ghost: [
        'text-[var(--text-muted)] hover:text-[var(--text)]',
        'hover:bg-[color-mix(in_srgb,var(--bg-muted)40%,transparent)]',
        'focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]'
      ].join(' '),
      link: 'text-[var(--accent-primary)] underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] focus:rounded'
    },
    size: {
      sm: 'px-2 py-1 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg'
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md'
  }
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading = false, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants }; 